import Vue from "vue";
import VueRouter from "vue-router";
import LayoutMain from "../layouts/LayoutMain.vue";
import { setPageMeta } from "../utils/meta";
import { bookingBase } from "../utils/market";

Vue.use(VueRouter);

// Customer token = customers.uuid
const TOKEN =
  ":token([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})";

// Generic share-preview text from the brief (never personal data)
const ORDER_FORM_META = {
  title: "Order form",
  description: "Tell us what you're bringing, then book a local drop-off or post your tapes to us.",
};

const routes = [
  {
    path: "/",
    component: LayoutMain,
    children: [
      {
        path: "",
        name: "HomeView",
        component: () =>
          import(
            /* webpackChunkName: "order-form-view" */ "@/views/OrderFormView.vue"
          ),
        meta: ORDER_FORM_META,
      },
      {
        path: "order",
        name: "OrderFormView",
        component: () =>
          import(
            /* webpackChunkName: "order-form-view" */ "@/views/OrderFormView.vue"
          ),
        props: true,
        meta: ORDER_FORM_META,
      },
      {
        // The AI estimate page showed estimated totals; quotes are manual
        // now. The photo quote lives on the booking domain (t2d_booking).
        path: "tape-estimate",
        name: "TapeEstimateView",
        beforeEnter(to, from, next) {
          const query = window.location.pathname.endsWith("tape-estimate") ? window.location.search : "";
          window.location.replace(`${bookingBase()}/tape-estimate${query || ""}`);
          next(false);
        },
      },
      {
        path: "privacy-policy",
        name: "PrivacyPolicyView",
        component: () =>
          import(
            /* webpackChunkName: "privacy-policy-view" */ "@/views/PrivacyPolicyView.vue"
          ),
        props: true,
        meta: {
          title: "Privacy Policy",
        },
      },
      {
        path: "terms-of-service",
        name: "TermsOfServiceView",
        component: () =>
          import(
            /* webpackChunkName: "terms-of-service" */ "@/views/TermsOfServiceView.vue"
          ),
        props: true,
        meta: {
          title: "Terms of service",
        },
      },
      {
        // Customer's quote link (quote_link)
        path: `${TOKEN}/quote`,
        name: "QuoteView",
        component: () =>
          import(/* webpackChunkName: "quote-view" */ "@/views/QuoteView.vue"),
        meta: {
          title: "Your quote",
          description:
            "Counted by our team from your photos. Book a local drop-off or post your tapes when you're ready.",
          personal: true,
        },
      },
      {
        // Customer's posting page (link_post)
        path: `${TOKEN}/post`,
        name: "PostView",
        component: () =>
          import(/* webpackChunkName: "post-view" */ "@/views/PostView.vue"),
        meta: {
          title: "Posting your tapes",
          description:
            "Our postal address, packing tips and a place to add your tracking number.",
          personal: true,
        },
      },
      {
        // Customer's own order link, e.g. /f1404e66-6ab7-4c6a-b483-0e4222de7e23
        // (booking_items_link; resume adds ?resumeGuid=…). Only matches a
        // UUID so location slugs below still work.
        path: TOKEN,
        name: "CustomerOrderFormView",
        component: () =>
          import(/* webpackChunkName: "order-link-view" */ "@/views/OrderLinkView.vue"),
        meta: {
          title: "Your order",
          description:
            "Your details and items are saved. Choose drop-off or post, or add photos of your tapes.",
          personal: true,
        },
      },
      {
        // Location slug straight off the root, e.g. /farnborough-gu14-8jj-whitehouse-close.
        // Must stay after the fixed pages above so they still match first.
        path: ":location",
        name: "LocationOrderFormView",
        component: () =>
          import(
            /* webpackChunkName: "order-form-view" */ "@/views/OrderFormView.vue"
          ),
        props: true,
        meta: ORDER_FORM_META,
      },
      {
        path: "/:catchAll(.*)",
        name: "ErrorNotFoundView",
        component: () =>
          import(
            /* webpackChunkName: "not-found-view" */ "@/views/error/ErrorNotFoundView.vue"
          ),
        props: true,
        meta: {
          title: "Page not found",
          noindex: true,
        },
      },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    // If there's a saved position (e.g., navigating back), use it
    if (savedPosition) {
      return savedPosition;
    }
    // Otherwise, scroll to the top
    return { x: 0, y: 0 };
  },
});
router.afterEach((to) => {
  //BOC:[page title + meta] personal pages are noindex with generic titles
  Vue.nextTick(() => {
    setPageMeta({
      title: to.meta.title,
      description: to.meta.description,
      noindex: !!(to.meta.personal || to.meta.noindex),
    });
  });
  //EOC
});

export default router;
